import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Decimal } from 'decimal.js';
import { type ReactNode, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  useEdiPurchaseOrderDelete,
  useEdiPurchaseOrderDetail,
  useEdiPurchaseOrderUpdate,
} from '@/api/hanly-v2/edi/edi';
import {
  type EdiPurchaseOrderDetailResponse,
  EdiPurchaseOrderStatusEnum,
} from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { MobilePageHeader } from '@/components/mobile-page-header';
import { Button, LinkButton } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { useShowPrices } from '@/contexts/auth';
import { toOrderItemDisplayData } from '@/features/orders/display-data';
import { OrderConfirmModal } from '@/features/orders/order-confirm-modal';
import { OrderFormBody } from '@/features/orders/order-form-body';
import { OrderLinesView } from '@/features/orders/order-lines-view';
import { OrderTotals } from '@/features/orders/order-totals';
import { type OrderDetailForm, orderDetailFormSchema } from '@/features/orders/schema';
import { StatusBadge } from '@/features/orders/status-badge';
import { calculateOrderTotals, type OrderItemDisplayData } from '@/features/orders/types';
import { formatYMD, formatYMDhm, formatYMDSlash } from '@/utils/date';

const SummaryItem = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col">
    <span className="text-sm text-neutral-600">{label}</span>
    <span>{children}</span>
  </div>
);

interface OrderSummaryProps {
  order: EdiPurchaseOrderDetailResponse;
  lineCount: number;
  showPrices: boolean;
}

const OrderSummary = ({ order, lineCount, showPrices }: OrderSummaryProps) => (
  <section className="flex flex-col gap-3 border border-neutral-200 bg-white p-4">
    <div className="flex items-center justify-between gap-3">
      <StatusBadge status={order.status} />
      {order.status === EdiPurchaseOrderStatusEnum.PENDING && (
        <span className="text-sm text-neutral-600">確定前のため変更・取り消しできます</span>
      )}
    </div>
    {showPrices && (
      <div className="text-2xl font-bold">
        ¥{Number(order.total_amount).toLocaleString()}
        <span className="ml-1 text-sm font-normal text-neutral-600">（税込）</span>
      </div>
    )}
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
      <SummaryItem label="注文日時">
        {order.status === EdiPurchaseOrderStatusEnum.PENDING ? '-' : formatYMDhm(order.created_at)}
      </SummaryItem>
      <SummaryItem label="希望納期">
        {order.desired_delivery_date ? formatYMDSlash(order.desired_delivery_date) : '即納希望'}
      </SummaryItem>
      <SummaryItem label="最終更新">{formatYMDhm(order.updated_at)}</SummaryItem>
      <SummaryItem label="品目数">{lineCount}品目</SummaryItem>
    </div>
  </section>
);

interface OrderDetailViewProps {
  order: EdiPurchaseOrderDetailResponse;
  items: OrderItemDisplayData[];
  onEdit: () => void;
  onCancelOrder: () => void;
}

const OrderDetailView = ({ order, items, onEdit, onCancelOrder }: OrderDetailViewProps) => {
  const showPrices = useShowPrices();
  const isPending = order.status === EdiPurchaseOrderStatusEnum.PENDING;
  const lineCount = items.filter((item) => item.quantity > 0).length;
  const totals = calculateOrderTotals(items);

  const actions = isPending ? (
    <>
      <Button variant="dangerOutlined" size="lg" onClick={onCancelOrder}>
        注文を取り消す
      </Button>
      <Button size="lg" onClick={onEdit} className="flex-1 md:flex-none">
        内容を変更する
      </Button>
    </>
  ) : (
    <LinkButton to={`/orders/new?from=${order.id}`} size="lg" className="flex-1 md:flex-none">
      <ArrowPathIcon className="size-5" />
      この内容で再注文
    </LinkButton>
  );

  return (
    <div className="flex flex-col gap-4 pb-24 md:pb-0">
      <div className="hidden items-center justify-between gap-3 md:flex">
        <h2>EDI No. {order.id}</h2>
        <div className="flex gap-2">{actions}</div>
      </div>
      <OrderSummary order={order} lineCount={lineCount} showPrices={showPrices} />
      <section className="flex flex-col gap-2">
        <h3 className="text-base">注文商品</h3>
        <OrderLinesView items={items} itemDisplayColumns={order.item_display_columns} />
      </section>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <section className="flex flex-col gap-1 md:max-w-xl">
          <h3 className="text-base">備考</h3>
          <p className="text-sm whitespace-pre-wrap">{order.notes || '-'}</p>
        </section>
        {showPrices && (
          <div className="border border-neutral-200 bg-white p-4 md:w-96">
            <OrderTotals totals={totals} />
          </div>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-neutral-200 bg-white px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden">
        {actions}
      </div>
    </div>
  );
};

export const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const navigate = useNavigate();

  const { data: order, isLoading, isError, refetch } = useEdiPurchaseOrderDetail(orderId);
  const updateMutation = useEdiPurchaseOrderUpdate();
  const deleteMutation = useEdiPurchaseOrderDelete();

  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isCancelSheetOpen, setIsCancelSheetOpen] = useState(false);

  const formValues = useMemo<OrderDetailForm | undefined>(
    () =>
      order
        ? {
            deliveryMode: order.desired_delivery_date ? 'date' : 'asap',
            desiredDeliveryDate: order.desired_delivery_date ? formatYMD(order.desired_delivery_date) : '',
            notes: order.notes,
            items: order.items.map((item) => ({
              id: item.id,
              quantity: new Decimal(item.quantity ?? '0').toNumber(),
              desired_delivery_date: item.desired_delivery_date ?? null,
            })),
          }
        : undefined,
    [order],
  );

  const methods = useForm<OrderDetailForm>({
    resolver: zodResolver(orderDetailFormSchema),
    values: formValues,
  });

  const watchedItems = useWatch({ control: methods.control, name: 'items' });
  const watchedNotes = useWatch({ control: methods.control, name: 'notes' });

  const savedItems = useMemo(
    () =>
      (order?.items ?? []).map((item) =>
        toOrderItemDisplayData(
          item,
          new Decimal(item.quantity ?? '0').toNumber(),
          item.desired_delivery_date ?? null,
        ),
      ),
    [order?.items],
  );

  const editingItems = useMemo(
    () =>
      (order?.items ?? []).map((item, index) =>
        toOrderItemDisplayData(
          item,
          watchedItems?.[index]?.quantity || 0,
          watchedItems?.[index]?.desired_delivery_date ?? null,
        ),
      ),
    [order?.items, watchedItems],
  );

  const totals = useMemo(() => calculateOrderTotals(editingItems), [editingItems]);

  const watchedDeliveryMode = useWatch({ control: methods.control, name: 'deliveryMode' });
  const watchedDeliveryDate = useWatch({ control: methods.control, name: 'desiredDeliveryDate' });

  const deliveryDateLabel = useMemo(() => {
    if (watchedDeliveryMode === 'asap') return '即納希望';
    return watchedDeliveryDate ? formatYMDSlash(watchedDeliveryDate) : '-';
  }, [watchedDeliveryMode, watchedDeliveryDate]);

  const buildUpdatePayload = (formData: OrderDetailForm) => ({
    desired_delivery_date:
      formData.deliveryMode === 'date'
        ? formData.desiredDeliveryDate
          ? new Date(`${formData.desiredDeliveryDate}T00:00:00`).toISOString()
          : undefined
        : null,
    notes: formData.notes,
    items: formData.items.map((item) => ({
      id: item.id,
      quantity: String(item.quantity),
      desired_delivery_date: item.desired_delivery_date,
    })),
  });

  const handleOpenConfirmModal = methods.handleSubmit(() => {
    setIsConfirmModalOpen(true);
  });

  const handleConfirm = async () => {
    const formData = methods.getValues();
    try {
      await updateMutation.mutateAsync({ id: orderId, data: buildUpdatePayload(formData) });
      toast.success('注文を更新しました');
      setIsConfirmModalOpen(false);
      setIsEditing(false);
      refetch();
    } catch {
      toast.error('エラーが発生しました');
    }
  };

  const handleCancelOrder = async () => {
    try {
      await deleteMutation.mutateAsync({ id: orderId });
      toast.success('注文を取り消しました');
      navigate('/orders');
    } catch {
      toast.error('エラーが発生しました');
    }
  };

  const handleCancelEdit = () => {
    methods.reset(formValues);
    setIsEditing(false);
  };

  const breadcrumbs = (
    <Breadcrumbs
      items={[{ label: 'ホーム', to: '/' }, { label: '発注', to: '/orders' }, { label: `${id}` }]}
    />
  );

  if (isLoading) {
    return <div className="py-8 text-center text-neutral-600">読み込み中...</div>;
  }

  if (isError || !order) {
    return (
      <div>
        <MobilePageHeader title={`EDI No. ${id}`} backTo="/orders" />
        {breadcrumbs}
        <div className="py-8 text-center text-red-500">データの取得に失敗しました</div>
      </div>
    );
  }

  const isEditable = isEditing && order.status === EdiPurchaseOrderStatusEnum.PENDING;

  return (
    <div className="flex-1 bg-neutral-50 md:bg-white">
      <MobilePageHeader title={isEditable ? '注文内容の変更' : `EDI No. ${order.id}`} backTo="/orders" />
      {breadcrumbs}

      <div className="mx-auto max-w-[1440px] px-3 pt-3 md:px-8 md:pt-4 md:pb-10">
        {isEditable ? (
          <FormProvider {...methods}>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="hidden md:block">EDI No. {order.id} の変更</h2>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="ml-auto h-10 cursor-pointer text-sm text-sky-800 underline hover:opacity-60"
                >
                  変更をやめる
                </button>
              </div>
              <OrderFormBody
                items={editingItems}
                itemDisplayColumns={order.item_display_columns}
                totals={totals}
                onConfirmClick={handleOpenConfirmModal}
                emptyMessage="明細がありません"
                initialFilter="entered"
              />
              <OrderConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirm}
                isLoading={updateMutation.isPending}
                deliveryDateLabel={deliveryDateLabel}
                notes={watchedNotes ?? ''}
                items={editingItems}
                itemDisplayColumns={order.item_display_columns}
                totals={totals}
              />
            </form>
          </FormProvider>
        ) : (
          <OrderDetailView
            order={order}
            items={savedItems}
            onEdit={() => setIsEditing(true)}
            onCancelOrder={() => setIsCancelSheetOpen(true)}
          />
        )}
      </div>

      <Sheet
        open={isCancelSheetOpen}
        onClose={deleteMutation.isPending ? () => undefined : () => setIsCancelSheetOpen(false)}
        title="注文を取り消しますか？"
        className="md:max-w-md"
        footer={
          <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
            <Button
              variant="outlined"
              size="lg"
              onClick={() => setIsCancelSheetOpen(false)}
              disabled={deleteMutation.isPending}
            >
              やめる
            </Button>
            <Button variant="danger" size="lg" onClick={handleCancelOrder} loading={deleteMutation.isPending}>
              取り消す
            </Button>
          </div>
        }
      >
        <p className="text-sm">EDI No. {order.id} の注文を取り消します。取り消した注文は元に戻せません。</p>
      </Sheet>
    </div>
  );
};
